import _ from 'lodash';
import React from 'react';

// hooks
import { useParams } from 'react-router-dom';

// platform helpers
import { useJQuery } from '../../../utils/appUtils';
import { PubSub } from '../../../utils/eventUtils';

// actions
import { getPolicyDetailsWithFirmo } from '../../../policies/PolicyService';
import {
  getPaymentMethods,
  getTitleDesc,
  getXpressStatus,
  getPaymentStatus,
  getBalanceOnPolicy,
} from '../_services/BillingService';
// components
import { QuoteStepLoader } from '../quotes/QuoteStepLoader';
import { useActor } from '../../../components/hooks/useActor';
import useNotifications from '../../../_lib/notifications/useNotifications';

/**
 * @name withDetailsDependencies
 * @description This component is meant to enable the data combination and preperation for rendering
 */
export const withOneIncDependencies = () => {
  return (Component: any) => {
    return (props: any) => {
      const [data, setData] = React.useState('');
      const [balance, setBalance] = React.useState(0);
      const [downPayment, setPayment] = React.useState(0);
      const [titleData, setTitleData] = React.useState(true);
      // @ts-expect-error TS(2339): Property 'policyId' does not exist on type '{}'.
      const { policyId, product } = useParams();
      const actor = useActor();

      const dependencyParams = React.useMemo(
        () => ({
          policyId,
          product,
          setData,
          setBalance,
          setPayment,
          setTitleData,
        }),
        // eslint-disable-next-line
        [policyId, product]
      );

      useGetDependencies(dependencyParams);
      const { isLoaded } = useJQuery();

      if (!data) {
        return (
          <section>
            <QuoteStepLoader />
            <div id="portalOneContainer" />
          </section>
        );
      }

      if (data && !isLoaded) {
        return <section>Payment not available</section>;
      }

      if (data && isLoaded) {
        return (
          <Component
            {...props}
            data={data}
            balance={balance}
            downPayment={downPayment}
            titleData={titleData}
            customer={actor}
            product={product}
            policyId={policyId}
            isLoaded={isLoaded}
          />
        );
      }
    };
  };
};

function useGetDependencies({
  policyId,
  product,
  setData,
  setBalance,
  setPayment,
  setTitleData,
}: any) {
  const notifications = useNotifications();

  // on initial load
  React.useEffect(() => {
    getTitleDesc({ policyId })
      .then((resp: any) => {
        setTitleData(resp.data);
      })
      .then(() => {
        return getBalanceOnPolicy({ policyId })
          .then((resp: any) => {
            setBalance(resp.data);
            return resp.data;
          })
          .catch(() => {
            setBalance(0);
            return 0;
          });
      })
      .then((array: any) => {
        return getPaymentStatus({ policyId }).then((resp: any) => {
          return [array, resp.data];
        });
      })
      .then((array: any) => {
        return getXpressStatus().then((resp: any) => {
          array.push(resp.data);
          return array;
        });
      })
      .then((array: any) => {
        return getPolicyDetailsWithFirmo(policyId, product).then(({ data }) => {
          const brokerFee = _.isUndefined(data.brokerFee) ? 0 : data.brokerFee;
          const mgaFee = _.isUndefined(data.mgaFee) ? 0 : data.mgaFee;
          setPayment(
            (
              (array[0] - brokerFee - mgaFee) * 0.2 +
              brokerFee +
              mgaFee
            ).toFixed(2)
          );

          array.push(data);
          return array;
        });
      })
      .then((array: any) => {
        return getPaymentMethods().then((payment: any) => {
          const typeMappings = {
            'Credit Card': 'creditCard',
            'Bank Account': 'bankAccount',
          };
          const payments = payment.data.map((item: any) => {
            return {
              label: item.title,
              value: {
                ptype: typeMappings[item.type],
                token: item.tokenId,
              },
            };
          });
          array.push(payments);

          setData(array);
        });
      })
      .catch(() => {
        notifications.error(
          'Payment not available at this time. Please try again later'
        );
      });

    const sub = PubSub.subscribe('financing:accpeted', () => {
      getTitleDesc({ policyId }).then((resp: any) => {
        setTitleData(resp.data);
      });
      getBalanceOnPolicy({ policyId }).then((resp: any) => {
        setBalance(resp.data);
      });
    });
    return () => {
      sub.remove();
    };

    // eslint-disable-next-line
  }, []);
}
